import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { hasCode } from "@rekati/api";
import { Button, Skeleton, Spinner } from "@rekati/ui";
import { useI18n } from "../content/i18n";
import { LoginPage } from "../features/auth/LoginPage";
import { staffSession, useStaffSession } from "../features/auth/session-store";
import { ConsoleShell } from "../features/shell/ConsoleShell";
import { AREAS, canOpen, HOME, type Area, type AreaId } from "./access";
import { staffApi } from "./api";

/**
 * One sign-in, the role decides the view (docs/frontend-integration.md 6.1).
 * `GET /v1/staff/me` confirms a cached token on load and is the authority on
 * the role; a 401 anywhere sends the device back to /login.
 */
export function App() {
  const { t } = useI18n();
  return (
    <>
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-volt px-4 py-2 font-medium text-volt-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        {t("meta.skip")}
      </a>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<SignedIn>{(role) => <Navigate to={HOME[role]} replace />}</SignedIn>} />
        {AREAS.map((area) => (
          <Route
            key={area.id}
            path={area.path}
            element={<SignedIn>{(role) => <AreaRoute area={area} role={role} />}</SignedIn>}
          />
        ))}
        <Route path="*" element={<SignedIn>{(role) => <NotFound home={HOME[role]} />}</SignedIn>} />
      </Routes>
    </>
  );
}

function SignedIn({ children }: { children: (role: Area["roles"][number]) => ReactNode }) {
  const session = useStaffSession();
  const location = useLocation();
  const me = useQuery({
    // Keyed by token: one login's answer is never read as another's.
    queryKey: ["staff", "me", session?.token],
    queryFn: staffApi.me,
    enabled: Boolean(session),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (me.data) staffSession.updateStaff(me.data);
  }, [me.data]);

  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (me.isPending)
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner />
      </div>
    );
  if (me.isError && hasCode(me.error, "UNAUTHORIZED")) return <Navigate to="/login" replace />;
  return <>{children(session.staff.role)}</>;
}

/** Named export to a lazy component. */
const page = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(async () => ({ default: (await load())[name] }));

/*
 * One chunk per area, so a desk tablet downloads the door and never the
 * superuser's pages, and the board loads only the board.
 */
const PAGES: Record<AreaId, ComponentType> = {
  scan: page(() => import("../features/door/ScanPage"), "ScanPage"),
  lookup: page(() => import("../features/door/LookupPage"), "LookupPage"),
  "walk-in": page(() => import("../features/door/WalkInPage"), "WalkInPage"),
  board: page(() => import("../features/board/BoardPage"), "BoardPage"),
  overview: page(() => import("../features/numbers/OverviewPage"), "OverviewPage"),
  attendees: page(() => import("../features/numbers/AttendeesPage"), "AttendeesPage"),
  sessions: page(() => import("../features/numbers/SessionsPage"), "SessionsPage"),
  demographics: page(() => import("../features/numbers/DemographicsPage"), "DemographicsPage"),
  users: page(() => import("../features/manage/UsersPage"), "UsersPage"),
  event: page(() => import("../features/manage/EventPage"), "EventPage"),
  activities: page(() => import("../features/manage/ActivitiesPage"), "ActivitiesPage"),
  content: page(() => import("../features/manage/ContentPage"), "ContentPage"),
  email: page(() => import("../features/manage/EmailPage"), "EmailPage"),
  survey: page(() => import("../features/manage/SurveyPage"), "SurveyPage"),
};

function AreaRoute({ area, role }: { area: Area; role: Area["roles"][number] }) {
  // A role on a page that is not its own goes home, not to an error (6.1: 403 is "not for this login").
  if (!canOpen(role, area)) return <Navigate to={HOME[role]} replace />;
  const Screen = PAGES[area.id];
  const content = (
    <Suspense fallback={<Skeleton className="mx-auto h-64 w-full max-w-4xl rounded-xl" />}>
      <Screen />
    </Suspense>
  );
  if (area.chromeless)
    return (
      <div data-theme="mural" className="min-h-dvh bg-background text-foreground">
        {content}
      </div>
    );
  return <ConsoleShell>{content}</ConsoleShell>;
}

function NotFound({ home }: { home: string }) {
  const { t } = useI18n();
  return (
    <ConsoleShell>
      <div className="grid max-w-md gap-4 py-20">
        <p className="font-mono text-sm text-muted-foreground">404</p>
        <h1 className="text-3xl font-semibold tracking-tight">{t("notFound.title")}</h1>
        <Button asChild variant="solid" className="w-fit">
          <Link to={home}>{t("notFound.home")}</Link>
        </Button>
      </div>
    </ConsoleShell>
  );
}
